import { useCallback, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Box, Column, Row, Screen, Tab, Tabs, Typography } from '@tempmee/react';
import { TabBadge } from '../../TabBadge';
import { BackButton, InputSelect } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { fireShiftsOverviewActionNeededTabClicked } from '~/posthogs/events';
import { OpenTab, PendingTab, ScheduledTab } from './components';
import { FilterOptions } from './Constants';
import { TABS, TShiftListTab, useShiftList } from './hooks';
import { TJobListPageRoutes, TJobListPageRoutesLocationState } from './Types';

export const JobList = () => {
  const params = useParams<TJobListPageRoutes>();
  const location = useLocation() as Partial<{ state: Partial<TJobListPageRoutesLocationState> }>;

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const [filterJobType, setFilterJobType] = useState<DashboardJobsType>(DashboardJobsType.ALL);

  const { activeTab, dashboardInfo, onChangeTab } = useShiftList({
    type: (location.state?.type || params.type) as DashboardJobsStatus,
    jobType: filterJobType,
  });

  const countOpen = dashboardInfo?.openCount || 0;
  const countPending = dashboardInfo?.pendingCount || 0;
  const countScheduled = dashboardInfo?.filledCount || 0;

  const handleTabChanged = useCallback(
    (tab: string) => {
      if (tab === TABS.PENDING) {
        fireShiftsOverviewActionNeededTabClicked();
      }
      onChangeTab(tab as TShiftListTab);
    },
    [onChangeTab],
  );

  return (
    <Screen variant="standard">
      <Screen.Header
        start={<BackButton />}
        title={isEnforcePermanentJobV2 ? 'Shifts & Interviews' : 'Shifts Overview'}
      />
      <Screen.Body>
        {/* Screen's standard card already supplies the page gutter (p-10) and header gap,
            so the tab bar needs no margins — gap handles the rhythm down to the list. */}
        <Column gap={6}>
          <Row align="center" justify="between">
            <Tabs defaultValue={TABS.OPEN} value={activeTab} onChange={handleTabChanged}>
              <Tab
                value={TABS.OPEN}
                data-cy="open-shifts-tab"
                label={
                  <>
                    Open <TabBadge count={countOpen || undefined} />
                  </>
                }
              />
              <Tab
                value={TABS.PENDING}
                data-cy="action-needed-tab"
                label={
                  <>
                    Action Needed <TabBadge count={countPending || undefined} activeColor="error" />
                  </>
                }
              />
              <Tab
                value={TABS.SCHEDULED}
                data-cy="scheduled-shifts-tab"
                label={
                  <>
                    Scheduled <TabBadge count={countScheduled || undefined} />
                  </>
                }
              />
            </Tabs>
            {isEnforcePermanentJobV2 && (
              <Row align="center" gap={2}>
                <Typography as="span" bold variant="body1">
                  Filter by:
                </Typography>
                <Box width="212px">
                  <InputSelect
                    isSelectVisible={false}
                    options={FilterOptions}
                    value={filterJobType}
                    setValue={(val) => setFilterJobType(val as DashboardJobsType)}
                  />
                </Box>
              </Row>
            )}
          </Row>
          {activeTab === TABS.OPEN && <OpenTab jobType={filterJobType} />}
          {activeTab === TABS.PENDING && <PendingTab jobType={filterJobType} />}
          {activeTab === TABS.SCHEDULED && <ScheduledTab jobType={filterJobType} />}
        </Column>
      </Screen.Body>
    </Screen>
  );
};
